import { joinWithAnd } from '@application/utils';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import {
  Card,
  ContactInfo,
  Modal,
  ModalCloseButton,
  Section,
  SectionSubtitle,
  SectionText,
  SectionTitle,
  SkeletonBox,
  Slideshow,
} from '..';
import * as Styled from './styled';
import type { BreederOverviewModalProps } from './types';

export * from './types';

export const BreederOverviewModal: FC<BreederOverviewModalProps> = (props) => {
  const { modal, loading, title, content = {}, slideshow, contactInfo, breedNames = [] } = props;
  const { translate } = useTranslation();

  const breedNamesJoined = useMemo(
    () => joinWithAnd(translate, breedNames),
    [breedNames, translate],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Section centerChildren>
          <SkeletonBox style={{ width: '100%', height: '45px' }} />

          <SkeletonBox style={{ width: '78px', height: '78px', borderRadius: '100%' }} />
          <SkeletonBox style={{ width: '100%', height: '200px' }} />

          <Styled.SlideshowSkeletonContainer>
            <Styled.SlideshowSkeletonMainWrapper>
              <Styled.SlideshowSkeletonMain />
            </Styled.SlideshowSkeletonMainWrapper>
          </Styled.SlideshowSkeletonContainer>

          <SkeletonBox style={{ width: '100%', height: '100px' }} />
        </Section>
      );
    }

    return (
      <>
        <Section centerChildren>
          <SectionTitle>{title}</SectionTitle>
          <ContactInfo {...contactInfo} />
        </Section>
        <Section>
          <Styled.SlideshowWrapper>
            <Slideshow {...slideshow} onImageClick={() => {}} />
          </Styled.SlideshowWrapper>
        </Section>
        <Section>
          <SectionTitle>{translate('breederDetails.content.title')}</SectionTitle>
          <SectionText
            dangerouslySetInnerHTML={{
              __html: translate('breederDetails.content.introWithoutLink').replace(
                '$breeds',
                breedNamesJoined,
              ),
            }}
          />
          <SectionText>{content.description}</SectionText>
          {Object.keys(content)
            .filter(
              (contentKey) =>
                !!content[contentKey as keyof typeof content] && contentKey !== 'description',
            )
            .map((contentKey, index) => (
              <Section key={index}>
                <SectionSubtitle>
                  {translate(`breederDetails.content.subtitle.${contentKey}`)}
                </SectionSubtitle>
                <SectionText>{content[contentKey as keyof typeof content]}</SectionText>
              </Section>
            ))}
        </Section>
      </>
    );
  };

  return (
    <Modal {...modal}>
      <Card size="medium" isModal>
        <ModalCloseButton modal={modal} />
        <Styled.Wrapper>{renderContent()}</Styled.Wrapper>
      </Card>
    </Modal>
  );
};
